import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { ApiFehler } from "../api/client";

type ToastArt = "erfolg" | "fehler" | "info";

interface Toast {
  id: number;
  art: ToastArt;
  titel: string;
  text?: string;
  aktion?: { label: string; ausfuehren: () => void };
}

interface ToastApi {
  erfolg: (titel: string, text?: string) => void;
  info: (titel: string, text?: string) => void;
  fehler: (fehler: unknown, titel?: string, aktion?: Toast["aktion"]) => void;
}

const ToastKontext = createContext<ToastApi | null>(null);

const DAUER: Record<ToastArt, number> = { erfolg: 4500, info: 5000, fehler: 9000 };

export function fehlerText(fehler: unknown): { text: string; requestId: string | null } {
  if (fehler instanceof ApiFehler) {
    const feldFehler = Object.entries(fehler.feldFehler);
    const details = feldFehler.length ? ` (${feldFehler.map(([f, m]) => `${f}: ${m}`).join("; ")})` : "";
    return { text: fehler.message + details, requestId: fehler.requestId };
  }
  if (fehler instanceof Error) return { text: fehler.message, requestId: null };
  return { text: "Unbekannter Fehler", requestId: null };
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const naechsteId = useRef(1);

  const entfernen = useCallback((id: number) => setToasts((alt) => alt.filter((t) => t.id !== id)), []);

  const zeigen = useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = naechsteId.current++;
      setToasts((alt) => [...alt.slice(-4), { ...toast, id }]);
      window.setTimeout(() => entfernen(id), DAUER[toast.art]);
    },
    [entfernen],
  );

  const api = useMemo<ToastApi>(
    () => ({
      erfolg: (titel, text) => zeigen({ art: "erfolg", titel, text }),
      info: (titel, text) => zeigen({ art: "info", titel, text }),
      fehler: (fehler, titel = "Fehler", aktion) => {
        const { text, requestId } = fehlerText(fehler);
        zeigen({ art: "fehler", titel, text: requestId ? `${text} · Ref: ${requestId.slice(0, 12)}` : text, aktion });
      },
    }),
    [zeigen],
  );

  return (
    <ToastKontext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
        aria-live="polite"
        role="status"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto rounded-xl border bg-white p-4 shadow-lg ${
              t.art === "fehler" ? "border-ampel-rot/40" : t.art === "erfolg" ? "border-ampel-gruen/40" : "border-neutral-200"
            }`}
          >
            <div className="flex items-start gap-3">
              <span aria-hidden className="mt-0.5 text-lg leading-none">
                {t.art === "fehler" ? "⚠️" : t.art === "erfolg" ? "✅" : "ℹ️"}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{t.titel}</p>
                {t.text && <p className="mt-0.5 break-words text-sm text-neutral-600">{t.text}</p>}
                {t.aktion && (
                  <button
                    type="button"
                    className="mt-2 text-sm font-semibold underline underline-offset-2"
                    onClick={() => {
                      t.aktion?.ausfuehren();
                      entfernen(t.id);
                    }}
                  >
                    {t.aktion.label}
                  </button>
                )}
              </div>
              <button
                type="button"
                aria-label="Meldung schließen"
                className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                onClick={() => entfernen(t.id)}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastKontext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastKontext);
  if (!api) throw new Error("useToast außerhalb des ToastProvider verwendet");
  return api;
}
